<script lang="ts">
  import { tick } from "svelte";
  import { route, routeHref } from "@/router";
  import { token, user, checking, logout, initSession } from "@/stores/session";
  import {
    balance,
    servers,
    initialLoading,
    feedback,
    refresh,
    refreshAccount,
    refreshServers,
    refreshCapacity,
    resetDashboard,
  } from "@/stores/dashboard";
  import { get } from "svelte/store";
  import {
    dashboardRealtimeHealthy,
    dashboardRealtimeSubscribed,
    startDashboardRealtime,
    subscribeDashboardInvalidations,
  } from "@/stores/dashboard-realtime";
  import { isBillingSettlementPending } from "@/server-state-policy";
  import { transitionalStatuses } from "@/config";
  import { formatNumber } from "@/utils/format";
  import Brand from "@/components/Brand.svelte";
  import LoadingScreen from "@/components/LoadingScreen.svelte";
  import LoadingState from "@/components/LoadingState.svelte";
  import FeedbackBanner from "@/components/FeedbackBanner.svelte";
  import Modal from "@/components/Modal.svelte";
  import AuthScreen from "@/pages/auth/AuthScreen.svelte";
  import OverviewPage from "@/pages/overview/OverviewPage.svelte";
  import AccountPage from "@/pages/account/AccountPage.svelte";
  import ServersPage from "@/pages/servers/ServersPage.svelte";
  import CreateServerPage from "@/pages/servers/CreateServerPage.svelte";
  import ServerDetailsPage, { type ServerSection } from "@/pages/servers/ServerDetailsPage.svelte";
  import PluginsPage from "@/pages/servers/PluginsPage.svelte";
  import AdminOverviewPage from "@/pages/admin/AdminOverviewPage.svelte";
  import AdminStatsPage from "@/pages/admin/AdminStatsPage.svelte";
  import AdminPaymentsPage from "@/pages/admin/AdminPaymentsPage.svelte";
  import AdminUsersPage from "@/pages/admin/AdminUsersPage.svelte";
  import AdminServersPage from "@/pages/admin/AdminServersPage.svelte";
  import AdminServerPage from "@/pages/admin/AdminServerPage.svelte";
  import AdminNodesPage from "@/pages/admin/AdminNodesPage.svelte";

  let dashboardLoadError = $state("");
  const canManageServers = $derived($user && ($user.role === "admin" || $user.role === "operator"));

  void initSession();

  const activeSection = $derived(
    $route.name.startsWith("admin-") || $route.name === "admin"
      ? "admin"
      : $route.name === "servers" ||
          $route.name === "server-create" ||
          $route.name.startsWith("server-")
        ? "servers"
        : $route.name === "overview"
          ? "overview"
          : "",
  );

  // Первичная загрузка данных после входа.
  $effect(() => {
    if (!$token) {
      resetDashboard();
      return;
    }
    dashboardLoadError = "";
    const currentToken = $token;
    const controller = new AbortController();
    refresh(controller.signal)
      .catch((error: Error) => {
        if ($token === currentToken && !controller.signal.aborted && error.name !== "AbortError") {
          dashboardLoadError = error.message;
        }
      })
      .finally(() => {
        if ($token === currentToken) {
          initialLoading.set(false);
        }
      });
    return () => controller.abort();
  });

  // Notifications reconcile only affected resources; periodic reads repair missed delivery.
  $effect(() => {
    const currentToken = $token;
    if (!currentToken) {
      return;
    }
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;
    let inFlight = false;
    let dirty = false;
    let failures = 0;
    let healthy = get(dashboardRealtimeHealthy);
    let pending = get(servers).some(
      (server) => transitionalStatuses.has(server.status) || isBillingSettlementPending(server),
    );
    const clear = () => {
      if (timer !== null) {
        clearTimeout(timer);
      }
      timer = null;
    };
    const schedule = () => {
      clear();
      if (stopped || document.hidden || inFlight) {
        return;
      }
      const base = pending ? 4_000 : healthy ? 120_000 : 30_000;
      const delay = Math.min(base * 2 ** Math.min(failures, 3), 120_000);
      timer = setTimeout(() => void reconcile(), delay + Math.floor(Math.random() * delay * 0.2));
    };
    const reconcile = async () => {
      clear();
      if (stopped || document.hidden) {
        return;
      }
      if (inFlight) {
        dirty = true;
        return;
      }
      inFlight = true;
      try {
        await (pending ? refreshServers(controller.signal) : refresh(controller.signal));
        failures = 0;
      } catch {
        failures++;
      } finally {
        inFlight = false;
        if (dirty && !stopped && !document.hidden) {
          dirty = false;
          void reconcile();
        } else {
          schedule();
        }
      }
    };
    const unwatch = subscribeDashboardInvalidations(async (event) => {
      if (stopped || get(token) !== currentToken) {
        return;
      }
      const requests: Promise<void>[] = [];
      if (event.account) {
        requests.push(refreshAccount(controller.signal));
      }
      if (event.servers) {
        requests.push(refreshServers(controller.signal));
      }
      if (event.capacity && get(route).name === "server-create") {
        requests.push(refreshCapacity(controller.signal));
      }
      await Promise.all(requests);
    });
    const stopRealtime = startDashboardRealtime(currentToken);
    const unhealth = dashboardRealtimeHealthy.subscribe((value) => {
      if (healthy === value) {
        return;
      }
      healthy = value;
      schedule();
    });
    const unservers = servers.subscribe((list) => {
      const next = list.some(
        (server) => transitionalStatuses.has(server.status) || isBillingSettlementPending(server),
      );
      if (pending === next) {
        return;
      }
      pending = next;
      schedule();
    });
    const visibility = () => {
      if (document.hidden) {
        dirty = false;
        clear();
      } else if (!get(dashboardRealtimeSubscribed)) {
        void reconcile();
      }
    };
    document.addEventListener("visibilitychange", visibility);
    schedule();
    return () => {
      stopped = true;
      clear();
      controller.abort();
      unwatch();
      unhealth();
      unservers();
      stopRealtime();
      document.removeEventListener("visibilitychange", visibility);
    };
  });

  let menuOpen = $state(false);

  function toggleMenu() {
    menuOpen = !menuOpen;
  }

  function closeMenu() {
    menuOpen = false;
  }

  function handleMobileLogout() {
    closeMenu();
    logout();
  }

  async function retryDashboard() {
    if (!$token || $initialLoading) {
      return;
    }
    const currentToken = $token;
    dashboardLoadError = "";
    initialLoading.set(true);
    try {
      await refresh();
    } catch (cause) {
      if ($token === currentToken) {
        dashboardLoadError = (cause as Error).message;
      }
    } finally {
      if ($token === currentToken) {
        initialLoading.set(false);
      }
    }
  }

  function dismissFeedback() {
    feedback.set(null);
  }

  function skipToContent(event: MouseEvent) {
    event.preventDefault();
    document.getElementById("main-content")?.focus();
  }

  let previousRoute = "";
  // Прокрутка вверх при смене маршрута и закрытие мобильного меню.
  $effect(() => {
    const routeKey = `${$route.name}:${$route.args.id ?? ""}`;
    const changed = previousRoute !== "" && previousRoute !== routeKey;
    previousRoute = routeKey;
    if (changed) {
      dismissFeedback();
      void tick().then(() => document.getElementById("main-content")?.focus());
    }
    menuOpen = false;
    window.scrollTo({ top: 0 });
  });
</script>
<Modal />
{#if $checking}
  <LoadingScreen />
{:else if !$token || !$user}
  <AuthScreen />
{:else}
  <a class="skip-link" href="#main-content" onclick={skipToContent}>Перейти к содержимому</a>
  <div class="app-shell">
    <header class="topbar">
      <Brand />
      <nav
        id="panel-navigation"
        class={`account-nav ${menuOpen ? "open" : ""}`}
        aria-label="Разделы панели"
      >
        <a
          class={activeSection === "overview" ? "active" : ""}
          aria-current={activeSection === "overview" ? "page" : undefined}
          href={routeHref("overview")}
          onclick={closeMenu}>Обзор</a
        >
        <a
          class={activeSection === "servers" ? "active" : ""}
          aria-current={activeSection === "servers" ? "page" : undefined}
          href={routeHref("servers")}
          onclick={closeMenu}>Серверы</a
        >
        {#if $user.role === "admin" || $user.role === "operator"}<a
            class={activeSection === "admin" ? "active" : ""}
            aria-current={activeSection === "admin" ? "page" : undefined}
            href={routeHref("admin-overview")}
            onclick={closeMenu}>Оператор</a
          >{/if}
        <div class="account-nav-mobile-user">
          <span class="status-dot" aria-hidden="true"></span>
          <a class="user-link" href={routeHref("account")} onclick={closeMenu}>{$user.username}</a>
          <span class="nav-credits-badge">{formatNumber($balance)} кр.</span>
          <button class="ghost-button" type="button" onclick={handleMobileLogout}>Выйти</button>
        </div>
      </nav>
      <a
        class="nav-credits nav-credits-mobile"
        href={routeHref("overview")}
        title="Баланс кредитов"
      >
        <span class="nav-credits-amount">{formatNumber($balance)}</span>
        <span class="nav-credits-unit">кр.</span>
      </a>
      <div class="topbar-user">
        <a class="nav-credits" href={routeHref("overview")} title="Баланс кредитов">
          <span class="nav-credits-amount">{formatNumber($balance)}</span>
          <span class="nav-credits-unit">кр.</span>
        </a>
        <span class="status-dot" aria-hidden="true"></span>
        <a class="user-link" href={routeHref("account")}>{$user.username}</a>
        <button class="ghost-button" type="button" onclick={logout}>Выйти</button>
      </div>
      <button
        class={`menu-burger ${menuOpen ? "open" : ""}`}
        type="button"
        aria-label={menuOpen ? "Закрыть меню" : "Открыть меню"}
        aria-expanded={menuOpen}
        aria-controls="panel-navigation"
        onclick={toggleMenu}
      >
        <span></span>
        <span></span>
        <span></span>
      </button>
    </header>

    {#if $feedback}
      <FeedbackBanner feedback={$feedback} onClose={dismissFeedback} />
    {/if}

    <main id="main-content" class="dashboard" tabindex="-1">
      {#if $initialLoading}
        <LoadingState message="Получаем данные аккаунта…" />
      {:else if dashboardLoadError}
        <section class="panel-card route-empty" role="alert">
          <p class="eyebrow">Загрузка данных</p>
          <h1>Не удалось загрузить панель</h1>
          <p>{dashboardLoadError}</p>
          <button class="button" type="button" onclick={retryDashboard}>Повторить загрузку</button>
        </section>
      {:else if $route.name === "overview"}
        <OverviewPage />
      {:else if $route.name === "account"}
        <AccountPage />
      {:else if $route.name === "servers"}
        <ServersPage />
      {:else if ($route.name === "admin" || $route.name === "admin-overview") && canManageServers}
        <AdminOverviewPage />
      {:else if $route.name === "admin-stats" && canManageServers}
        <AdminStatsPage />
      {:else if $route.name === "admin-payments" && canManageServers}
        <AdminPaymentsPage />
      {:else if ($route.name === "admin-users" || $route.name === "admin-user") && canManageServers}
        {#key $route.args.id ?? "list"}<AdminUsersPage />{/key}
      {:else if $route.name === "admin-servers" && canManageServers}
        <AdminServersPage />
      {:else if $route.name === "admin-server" && $route.args.id && canManageServers}
        {#key $route.args.id}<AdminServerPage />{/key}
      {:else if ($route.name === "admin-nodes" || $route.name === "admin-node") && canManageServers}
        {#key $route.args.id ?? "list"}<AdminNodesPage />{/key}
      {:else if $route.name === "server-create"}
        <CreateServerPage />
      {:else if $route.name === "server-plugins" && $route.args.id}
        <PluginsPage id={$route.args.id} />
      {:else if $route.name.startsWith("server-") && $route.args.id}
        <ServerDetailsPage
          id={$route.args.id}
          section={($route.name === "server-settings"
            ? "settings"
            : $route.name === "server-console"
              ? "console"
              : $route.name === "server-files"
                ? "files"
                : $route.name === "server-ai"
                  ? "ai"
                  : $route.name === "server-backups"
                    ? "backups"
                    : "home") as ServerSection}
        />
      {:else}
        <section class="panel-card route-empty">
          <p class="eyebrow">Личная панель / ошибка</p>
          <h1>Страница не найдена</h1>
          <p>Такого раздела нет в панели управления.</p>
          <a class="button" href={routeHref("overview")}>Вернуться на главную</a>
        </section>
      {/if}
    </main>
  </div>
{/if}

<style>
  .skip-link {
    position: fixed;
    top: 0.75rem;
    left: 0.75rem;
    z-index: 200;
    padding: 0.8rem 1rem;
    border: 1px solid var(--mars-light);
    color: var(--cream);
    background: var(--surface);
    transform: translateY(-200%);
  }
  .skip-link:focus {
    transform: none;
  }
  main:focus {
    outline: none;
  }

  .account-nav {
    display: flex;
    gap: 1.6rem;
  }

  .account-nav-mobile-user {
    display: none;
  }

  .menu-burger {
    display: none;
  }

  .account-nav a {
    padding: 0.5rem 0;
    border-bottom: 1px solid transparent;
    color: var(--dim);
    font: 500 0.72rem/1 var(--font-display);
    letter-spacing: 0.12em;
    text-transform: uppercase;
    text-decoration: none;
    transition:
      color 160ms ease,
      border-color 160ms ease;
  }

  .account-nav a:hover {
    color: var(--cream);
  }

  .account-nav a.active {
    color: var(--mars-light);
    border-color: var(--mars-light);
  }

  .nav-credits {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.28rem 0.65rem;
    border: 1px solid rgb(214 75 50 / 0.35);
    border-radius: 9999px;
    background: rgb(214 75 50 / 0.08);
    color: var(--cream);
    text-decoration: none;
    font-size: 0.78rem;
    line-height: 1;
    transition:
      background 160ms ease,
      border-color 160ms ease;
  }

  .nav-credits:hover {
    background: rgb(214 75 50 / 0.18);
    border-color: var(--mars-light);
  }

  .nav-credits-amount {
    font-weight: 600;
    color: var(--mars-light);
    letter-spacing: -0.02em;
  }

  .nav-credits-unit {
    color: var(--muted);
    font-size: 0.72rem;
  }

  .nav-credits-mobile {
    display: none;
  }

  .nav-credits-badge {
    color: var(--mars-light);
    font-weight: 600;
    font-size: 0.8rem;
  }

  .route-empty {
    display: grid;
    gap: 1rem;
    justify-items: start;
    padding: clamp(2rem, 5vw, 3.5rem);
  }

  .route-empty h1 {
    margin: 0;
    font: 500 clamp(1.8rem, 4vw, 2.8rem)/1 var(--font-display);
    letter-spacing: -0.06em;
    text-transform: uppercase;
  }

  .route-empty p {
    margin: 0;
    color: var(--muted);
  }

  @media (max-width: 640px) {
    .menu-burger {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 5px;
      width: 2.5rem;
      height: 2.5rem;
      padding: 0;
      border: 1px solid var(--line);
      background: transparent;
      cursor: pointer;
      z-index: 20;
    }

    .menu-burger span {
      display: block;
      width: 1.25rem;
      height: 2px;
      background: var(--cream);
      transition:
        transform 200ms ease,
        opacity 200ms ease;
    }

    .menu-burger.open span:nth-child(1) {
      transform: translateY(7px) rotate(45deg);
    }

    .menu-burger.open span:nth-child(2) {
      opacity: 0;
    }

    .menu-burger.open span:nth-child(3) {
      transform: translateY(-7px) rotate(-45deg);
    }

    .account-nav {
      display: none;
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      background: var(--surface);
      border-bottom: 1px solid var(--line-strong);
      flex-direction: column;
      gap: 0;
      padding: 0.5rem 1.25rem 1.25rem;
      box-shadow: 0 1rem 2rem rgb(0 0 0 / 50%);
    }

    .account-nav.open {
      display: flex;
    }

    .nav-credits-mobile {
      display: inline-flex;
      margin-left: auto;
      margin-right: 0.75rem;
    }

    .account-nav a {
      padding: 0.9rem 0;
      border-bottom: 1px solid var(--line);
      font-size: 0.85rem;
    }

    .account-nav-mobile-user {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.75rem;
      padding-top: 1rem;
      margin-top: 0.5rem;
      color: var(--muted);
      font-size: 0.85rem;
    }

    .account-nav-mobile-user .ghost-button {
      border: 1px solid var(--line);
      padding: 0.4rem 0.8rem;
      color: var(--mars-light);
    }
  }
</style>
